import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, FileSearch, Plus } from 'lucide-react';
import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { CaseError, type CaseRecordData, listCases } from '@/lib/cases';
import {
  computePanelKpis,
  deriveFirstName,
  pickGreetingSlot,
  pickRecentCases,
  pickUpcomingDeadlines,
  type GreetingSlot,
  type PanelKpis,
} from '@/lib/home_panel';
import { useIsAuthenticated } from '@/stores/session';
import { getPerfil, type PerfilData } from '@/lib/perfil';
import {
  ContinuarSection,
  EducationalFooter,
  FEATURES,
  PanelSkeleton,
  PrazosSection,
} from '@/components/Home/HomePanelSections';

const GREETING_LABEL: Record<GreetingSlot, string> = {
  morning: ptBR.homePanel.greetingMorning,
  afternoon: ptBR.homePanel.greetingAfternoon,
  evening: ptBR.homePanel.greetingEvening,
};

function pluralizeCaso(n: number): string {
  return n === 1 ? ptBR.homePanel.casoSingular : ptBR.homePanel.casoPlural;
}

function formatGreeting(slot: GreetingSlot, name: string, kpis: PanelKpis): string {
  const greeting = name === '' ? ptBR.homePanel.greetingFallback : GREETING_LABEL[slot];
  return ptBR.homePanel.greetingTemplate
    .replace('{{greeting}}', greeting)
    .replace('{{name}}', name)
    .replace('{{ativos}}', String(kpis.ativos))
    .replace('{{ativosLabel}}', pluralizeCaso(kpis.ativos))
    .replace('{{aguardando}}', String(kpis.aguardandoLaudo))
    .replace('{{aguardandoLabel}}', pluralizeCaso(kpis.aguardandoLaudo));
}

interface KpiTileProps {
  label: string;
  value: string;
  testId: string;
  hint?: string;
  unavailable?: boolean;
}

function KpiTile({ label, value, testId, hint, unavailable }: KpiTileProps) {
  return (
    <div
      className="flex flex-col gap-1 border border-border bg-surface p-4"
      data-testid={testId}
      data-unavailable={unavailable ? 'true' : 'false'}
      title={unavailable ? hint : undefined}
    >
      <span className="text-caption uppercase tracking-wide text-fg-muted">{label}</span>
      <span className="font-mono text-display-sm text-fg">{value}</span>
      {unavailable && hint ? <span className="text-caption text-fg-muted">{hint}</span> : null}
    </div>
  );
}

interface PanelData {
  cases: CaseRecordData[] | null;
  perfil: PerfilData | null;
  error: string | null;
}

function useHomePanelData(): PanelData {
  const apiClient = useApiClient();
  const [data, setData] = useState<PanelData>({ cases: null, perfil: null, error: null });

  useEffect(() => {
    let cancelled = false;
    setData({ cases: null, perfil: null, error: null });
    Promise.all([listCases(apiClient), getPerfil(apiClient).catch(() => null)])
      .then(([list, p]) => {
        if (cancelled) return;
        setData({ cases: list, perfil: p, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          err instanceof CaseError && err.reason === 'network'
            ? ptBR.errors.networkOffline
            : ptBR.homePanel.summaryError;
        setData({ cases: [], perfil: null, error: msg });
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient]);

  return data;
}

function HomePanel() {
  const navigate = useNavigate();
  const { cases, perfil, error } = useHomePanelData();

  const now = useMemo(() => new Date(), []);
  const kpis = useMemo<PanelKpis>(
    () =>
      cases === null
        ? { casosNoMes: 0, ativos: 0, aguardandoLaudo: 0 }
        : computePanelKpis(cases, now),
    [cases, now],
  );
  const recent = useMemo(() => (cases ? pickRecentCases(cases) : []), [cases]);
  const deadlines = useMemo(() => (cases ? pickUpcomingDeadlines(cases, now) : []), [cases, now]);
  const firstName = useMemo(() => deriveFirstName(perfil?.email), [perfil]);
  const greeting = useMemo(
    () => formatGreeting(pickGreetingSlot(now), firstName, kpis),
    [now, firstName, kpis],
  );

  if (cases === null) {
    return (
      <section
        data-testid="home-page"
        data-variant="panel-loading"
        aria-labelledby="home-title"
        className="mx-auto max-w-6xl py-8 lg:py-10"
      >
        <h1 id="home-title" className="sr-only">
          {ptBR.productName}
        </h1>
        <PanelSkeleton />
      </section>
    );
  }

  return (
    <section
      data-testid="home-page"
      data-variant="panel"
      aria-labelledby="home-title"
      className="mx-auto max-w-6xl py-8 lg:py-10"
    >
      <header className="flex flex-wrap items-start justify-between gap-4">
        <h1
          id="home-title"
          className="font-display text-[38px] leading-none text-fg"
          data-testid="home-greeting"
        >
          {greeting}
        </h1>
        <div className="flex gap-2.5">
          <Button
            type="button"
            onClick={() => navigate('/casos/novo')}
            className="h-[38px]"
            data-testid="home-cta-novo"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {ptBR.homePanel.ctaPrimary}
          </Button>
          <Button asChild variant="secondary" className="h-[38px]">
            <Link to="/casos" data-testid="home-cta-ver-casos">
              {ptBR.homePanel.ctaSecondary}
            </Link>
          </Button>
        </div>
      </header>

      {error ? (
        <p
          role="alert"
          data-testid="home-panel-error"
          className="mt-6 border border-danger-500 bg-danger-50 px-3 py-2 text-body-sm text-danger-700"
        >
          {error}
        </p>
      ) : null}

      <div
        className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
        data-testid="home-kpis"
      >
        <KpiTile
          label={ptBR.homePanel.kpiCasosNoMes}
          value={String(kpis.casosNoMes)}
          testId="home-kpi-casos-no-mes"
        />
        <KpiTile
          label={ptBR.homePanel.kpiTempoMedio}
          value={ptBR.casos.placeholderEmDash}
          testId="home-kpi-tempo-medio"
          hint={ptBR.homePanel.kpiUnavailableHint}
          unavailable
        />
        <KpiTile
          label={ptBR.homePanel.kpiLrAutoria}
          value={ptBR.casos.placeholderEmDash}
          testId="home-kpi-lr-autoria"
          hint={ptBR.homePanel.kpiUnavailableHint}
          unavailable
        />
        <KpiTile
          label={ptBR.homePanel.kpiLrHeteroautoria}
          value={ptBR.casos.placeholderEmDash}
          testId="home-kpi-lr-heteroautoria"
          hint={ptBR.homePanel.kpiUnavailableHint}
          unavailable
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ContinuarSection items={recent} />
        <PrazosSection entries={deadlines} />
      </div>

      <EducationalFooter />
    </section>
  );
}

function HomeAnonymous() {
  return (
    <section
      data-testid="home-page"
      data-variant="anonymous"
      aria-labelledby="home-title"
      className="mx-auto max-w-3xl py-8 lg:py-12"
    >
      <div className="flex flex-col items-center text-center">
        <span className="inline-flex h-16 w-16 items-center justify-center border border-border bg-fg/[0.03] text-accent-600">
          <FileSearch className="h-8 w-8" aria-hidden="true" />
        </span>
        <h1 id="home-title" className="mt-6 text-display-lg text-fg">
          {ptBR.productName}
        </h1>
        <p className="mt-4 max-w-prose text-body-lg text-fg-muted">{ptBR.home.intro}</p>
        <Button asChild size="lg" className="mt-8">
          <Link to="/casos" data-testid="home-cta-start">
            {ptBR.home.ctaStart}
            <ArrowRight className="h-5 w-5" aria-hidden="true" />
          </Link>
        </Button>
      </div>

      <section aria-labelledby="home-features-title" className="mt-16">
        <h2 id="home-features-title" className="sr-only">
          {ptBR.home.featuresTitle}
        </h2>
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {FEATURES.map((feature) => (
            <li key={feature.title} className="border border-border bg-surface p-5 text-left">
              <feature.Icon className="h-6 w-6 text-accent-600" aria-hidden="true" />
              <h3 className="mt-3 text-heading-sm text-fg">{feature.title}</h3>
              <p className="mt-1 text-body-sm text-fg-muted">{feature.body}</p>
            </li>
          ))}
        </ul>
      </section>
    </section>
  );
}

export function HomePage() {
  const authenticated = useIsAuthenticated();
  return authenticated ? <HomePanel /> : <HomeAnonymous />;
}
